import type { ExportLimitsStatusState } from '@civilgrid/ui/components';
import { SITE_DATA_LAYER_ID_PREFIX } from '@civilgrid/utils/src/extract/constants';
import { cloneDeep } from 'lodash';
import { proxy } from 'valtio';
import { proxySet } from 'valtio/utils';
import {
  getDigitizationAccess as apiGetDigitizationAccess,
  getLayerAssetPresence as apiGetLayerAssetPresence,
  getProject as apiGetProject,
  getDownloadRestrictions,
} from '~/api/projects2';
import { captureTypedPosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import type { Project, TOCItem, TOCParent, TOC as TOCType } from '~/common/types';
import { calculateArea } from '~/helpers/unitHelpers';
import { layerIdToDownloadLayer } from '~/store2/downloadLayers';
import layerStore from '~/store2/layers';
import { CUSTOM_DATA_SECTION_ID, CUSTOM_DATA_SECTION_LABEL } from '../downloads/extract/customLayers';
import {
  SITE_DATA_SECTION_ID,
  SITE_DATA_SECTION_LABEL,
  toMapPreviewLayerId,
} from '../downloads/extract/siteDataLayers';
import { reportExportFormDataLoadFailure, SLOW_LOAD_THRESHOLD_MS } from '../downloads/loadTelemetry';
import { CUSTOM_DATA_LAYER_PREFIX } from '../map/customDataLayers.helpers';
import projectDetailsMapStore from '../map/projectDetailsMapStore';
import { getTOCParentSection } from './common';
import { expansionKey, type PickerGroup } from './pickerTree';

type TGeom = GeoJSON.Polygon | GeoJSON.MultiPolygon;

export type LayerPresenceScope = 'within' | 'outside' | 'all';

export type LimitsStatus = ExportLimitsStatusState;

// Longer than the server's limits budget, so a map server stall usually reports the server's own error.
export const EXPORT_LIMITS_TIMEOUT_MS = 40_000;

// One per request, off the proxy: aborting marks a late reply stale without cancelling the other request.
let restrictionsAbort: AbortController | undefined;
let digitizationAbort: AbortController | undefined;

// CG-752: in-memory copy of the map's visibility state, captured on download-mode entry and
// restored on exit.
type LayerVisibilitySnapshot = {
  visibleLayerIds: Set<string>;
  visibleLinkedOrganizationIds: Set<string>;
};

// initialize with all utility and telco layers (telco will be initially disabled in urban areas)
const initialLayers = [
  'electricTransmissionLines',
  'electricTransmissionStructures',
  'electricDistributionLines',
  'electricDistributionStructures',
  'electricDistributionServiceLines',
  'electricDistributionServiceVaults',
  'electricDistributionOther',
  'gasTransmission',
  'gasDistribution',
  'gasService',
  'gasPoints',
  'waterTransmission',
  'waterMain',
  'waterService',
  'waterPoints',
  'sanitaryPipes',
  'sanitaryPoints',
  'stormPipes',
  'stormPoints',
  'recycledWaterPipes',
  'recycledWaterPoints',
  'sanitaryPoints',
  'sanitaryPipes',
];

const initialSelectedLayerIds = new Set(initialLayers);
class NewDowloadStore {
  isLoading: boolean = false;
  project?: Project | undefined = undefined;
  downloadBoundary: TGeom | undefined;
  isProjectInUrbanArea: boolean = true;
  selectedLayerIds: Set<string> = proxySet<string>();
  restrictedLayerIds: Set<string> = proxySet<string>();
  layerIdsWithoutAssets: Set<string> = proxySet<string>();
  layerIdsWithUnknownAssets: Set<string> = proxySet<string>();
  isCheckingLayerAssets: boolean = false;
  // Tells a failed check apart from one that found every layer inside the boundary
  layerAssetPresenceFailed: boolean = false;
  // Bumped per request so an in-flight check that resolves after a reset is discarded
  layerAssetPresenceRequestId: number = 0;
  TOCLayers: TOCType = [];
  outsideAreaTOCLayers: TOCType = [];
  expandedSections: Set<string> = proxySet<string>();
  inNewDownloadMode: boolean = false;
  restrictions: { [key: string]: number } = {};
  hasDigitizationAccess: boolean = false;
  // Set only by the background limits load
  limitsStatus: LimitsStatus = 'idle';
  digitizationAccessStatus: LimitsStatus = 'idle';
  // Lets a project load that finishes after a reset skip starting its limits
  projectLoadId: number = 0;
  layerVisibilitySnapshot: LayerVisibilitySnapshot | undefined;
  // Custom-data leaf ids the extract picker currently offers (synced from its rows query).
  // Lets toggleAllLayers(true) turn them on even though they never live in TOCLayers.
  availableCustomLayerIds: Set<string> = proxySet<string>();
  // Site-data leaf ids the extract picker currently offers (empty unless its entitlement and flag
  // gates pass). Render-time only, like the custom-data set above.
  availableSiteDataLayerIds: Set<string> = proxySet<string>();

  constructor() {
    this.selectedLayerIds = proxySet<string>(initialSelectedLayerIds);
    this.rebuildTOCLayers();
  }

  reset() {
    this.isLoading = false;
    this.project = undefined;
    this.downloadBoundary = undefined;
    this.selectedLayerIds = proxySet<string>(initialSelectedLayerIds);
    this.restrictedLayerIds = proxySet<string>();
    this.availableCustomLayerIds = proxySet<string>();
    this.availableSiteDataLayerIds = proxySet<string>();
    // Cleared before TOCLayers is rebuilt so the next project starts from the full layer list
    this.layerIdsWithoutAssets.clear();
    this.layerIdsWithUnknownAssets.clear();
    this.isCheckingLayerAssets = false;
    this.layerAssetPresenceFailed = false;
    this.layerAssetPresenceRequestId++;
    this.rebuildTOCLayers();
    this.expandedSections.clear();
    this.inNewDownloadMode = false;
    this.hasDigitizationAccess = false;
    this.restrictions = {};
    restrictionsAbort?.abort();
    digitizationAbort?.abort();
    this.projectLoadId++;
    this.limitsStatus = 'idle';
    this.digitizationAccessStatus = 'idle';

    // CG-752: unpause FIRST so the post-restore subscription batch persists the restored set,
    // then wipe the download selection and put the snapshot back. MUTATE IN PLACE — never
    // reassign the proxySets (subscriptions are bound to the object instances).
    layerStore.pausePersistence = false;
    layerStore.suppressCustomDataDefaultOn = false;
    layerStore.toggleAllLayers(false);
    if (this.layerVisibilitySnapshot) {
      for (const id of this.layerVisibilitySnapshot.visibleLayerIds) {
        layerStore.visibleLayerIds.add(id);
      }
      for (const orgId of this.layerVisibilitySnapshot.visibleLinkedOrganizationIds) {
        layerStore.visibleLinkedOrganizationIds.add(orgId);
      }
      this.layerVisibilitySnapshot = undefined;
    }
  }

  setup() {
    // CG-752: snapshot the user's map state before wiping — reset() puts it back. Skip if a
    // snapshot is already held (re-entrant setup, e.g. legacy<->extract form swap mid-flight).
    if (!this.layerVisibilitySnapshot) {
      this.layerVisibilitySnapshot = {
        visibleLayerIds: new Set(layerStore.visibleLayerIds),
        visibleLinkedOrganizationIds: new Set(layerStore.visibleLinkedOrganizationIds),
      };
    }
    layerStore.pausePersistence = true;
    // The preview must mirror the picker: block CustomDataLayers' default-ON seeding while this
    // form owns map visibility, or unselected custom layers render on the preview map.
    layerStore.suppressCustomDataDefaultOn = true;

    this.rebuildTOCLayers();
    this.selectedLayerIds = proxySet<string>(initialSelectedLayerIds);
    // Stale across a legacy<->extract form swap; the extract tab re-syncs them from its own gates.
    this.availableCustomLayerIds = proxySet<string>();
    this.availableSiteDataLayerIds = proxySet<string>();

    // Disable main layer picker when this page is active, toggle all current layers off
    this.inNewDownloadMode = true;
    layerStore.toggleAllLayers(false);

    // Toggle all default download layers in the layer store
    const layersToAdd = Array.from(this.selectedLayerIds);
    layersToAdd.forEach((layerId) => {
      layerStore.visibleLayerIds.add(layerId);
    });
  }

  getTOCLayersConfig(presence: LayerPresenceScope = 'within') {
    return this.filterTOCItems(cloneDeep(layerStore.TOCLayers), presence);
  }

  rebuildTOCLayers() {
    this.TOCLayers = this.getTOCLayersConfig('within');
    this.outsideAreaTOCLayers = this.getTOCLayersConfig('outside');
  }

  /** With `asyncLimits`, resolves once the project loads and leaves the export limits to settle in the background. */
  async getProject(projectId: string, options?: { asyncLimits?: boolean }) {
    if (options?.asyncLimits) return this.loadProjectWithBackgroundLimits(projectId);

    // Fires while the load is still in flight; cleared below on any exit.
    const slowLoadTimer = setTimeout(() => {
      captureTypedPosthogEvent(POSTHOG_EVENTS.exportFormProjectSlowLoadDetected, { project_id: projectId });
    }, SLOW_LOAD_THRESHOLD_MS);

    try {
      this.isLoading = true;

      const project = await apiGetProject(projectId);

      if (project) {
        try {
          const [restrictions, access] = await Promise.all([
            getDownloadRestrictions(projectId),
            apiGetDigitizationAccess(projectId),
          ]);

          this.restrictions = restrictions;
          this.hasDigitizationAccess = access.hasDigitizationAccess;

          const projectArea = calculateArea({
            unitType: 'acres',
            unitSystem: 'imperial',
            geometry: project.geometry,
          });

          this.determineLayerRestrictions(this.TOCLayers, projectArea.value, restrictions);

          this.rebuildTOCLayers();
        } catch (e) {
          console.warn('Error fetching restrictions:  ', e);
          reportExportFormDataLoadFailure(projectId, 'restrictions', e);
        }

        // Not awaited: the check fans out an Esri query per layer, so the form renders immediately
        // and the picker shows its own progress. Presence lands after setup() has seeded the
        // default selection, which is what lets the prune remove hidden layers from it.
        void this.loadLayerAssetPresence(projectId);

        this.project = project;

        projectDetailsMapStore.setup();
      }
    } catch (e) {
      console.error('Error fetching project: ', e);
      reportExportFormDataLoadFailure(projectId, 'project', e);
    } finally {
      this.isLoading = false;
      clearTimeout(slowLoadTimer);
    }
  }

  async loadProjectWithBackgroundLimits(projectId: string) {
    const loadId = ++this.projectLoadId;
    const slowLoadTimer = setTimeout(() => {
      captureTypedPosthogEvent(POSTHOG_EVENTS.exportFormProjectSlowLoadDetected, { project_id: projectId });
    }, SLOW_LOAD_THRESHOLD_MS);

    try {
      this.isLoading = true;

      const project = await apiGetProject(projectId);

      if (loadId !== this.projectLoadId) return;

      if (project) {
        this.project = project;
        projectDetailsMapStore.setup();
        void this.loadRestrictions(projectId, project);
        void this.loadDigitizationAccess(projectId);
      }
    } catch (e) {
      if (loadId !== this.projectLoadId) return;
      console.error('Error fetching project: ', e);
      reportExportFormDataLoadFailure(projectId, 'project', e);
    } finally {
      if (loadId === this.projectLoadId) this.isLoading = false;
      clearTimeout(slowLoadTimer);
    }
  }

  /** Uses the loaded project, not the route, so the limits are measured against its boundary. */
  async retryLimits() {
    const project = this.project;
    if (!project) return;
    const requests = [this.loadRestrictions(project.id, project)];
    if (this.digitizationAccessStatus === 'failed') requests.push(this.loadDigitizationAccess(project.id));
    await Promise.all(requests);
  }

  async loadRestrictions(projectId: string, project: Project) {
    restrictionsAbort?.abort();
    const controller = new AbortController();
    restrictionsAbort = controller;
    this.limitsStatus = 'loading';

    try {
      const restrictions = await getDownloadRestrictions(projectId, {
        signal: controller.signal,
        timeout: EXPORT_LIMITS_TIMEOUT_MS,
      });
      if (controller.signal.aborted) return;

      this.restrictions = restrictions;
      const projectArea = calculateArea({ unitType: 'acres', unitSystem: 'imperial', geometry: project.geometry });
      this.restrictedLayerIds.clear();
      this.determineLayerRestrictions(this.TOCLayers, projectArea.value, restrictions);
      this.limitsStatus = 'loaded';
    } catch (e) {
      if (controller.signal.aborted) return;

      this.limitsStatus = 'failed';
      console.warn('Error fetching restrictions:  ', e);
      reportExportFormDataLoadFailure(projectId, 'restrictions', e);
    }
  }

  /** Starts the layer asset check once access settles, since its candidates depend on access. */
  async loadDigitizationAccess(projectId: string) {
    digitizationAbort?.abort();
    const controller = new AbortController();
    digitizationAbort = controller;
    this.digitizationAccessStatus = 'loading';

    try {
      const access = await apiGetDigitizationAccess(projectId, {
        signal: controller.signal,
        timeout: EXPORT_LIMITS_TIMEOUT_MS,
      });
      if (controller.signal.aborted) return;

      this.hasDigitizationAccess = access.hasDigitizationAccess;
      this.rebuildTOCLayers();
      this.digitizationAccessStatus = 'loaded';
    } catch (e) {
      if (controller.signal.aborted) return;

      this.digitizationAccessStatus = 'failed';
      console.warn('Error fetching digitization access: ', e);
      reportExportFormDataLoadFailure(projectId, 'digitization_access', e);
    }

    void this.loadLayerAssetPresence(projectId);
  }

  isTOCParent(item: any): item is TOCParent {
    return item.children;
  }

  // Keeps the downloadable layers in the given presence scope
  filterTOCItems(items: TOCItem[], presence: LayerPresenceScope = 'within'): TOCItem[] {
    return items
      .map((item) => {
        if (this.isTOCParent(item)) {
          const filteredChildren = this.filterTOCItems(item.children, presence);
          return { ...item, children: filteredChildren };
        } else {
          return item;
        }
      })
      .filter((item) => {
        if (this.isTOCParent(item)) {
          if (item.sectionId?.includes('digitized')) {
            if (this.hasDigitizationAccess === true) {
              return item.children.length > 0;
            } else {
              return false;
            }
          }

          return item.children.length > 0;
        } else {
          if (!layerIdToDownloadLayer[item.id]) {
            return false;
          }

          if (presence === 'within') {
            return !this.layerIdsWithoutAssets.has(item.id);
          }

          if (presence === 'outside') {
            return this.layerIdsWithoutAssets.has(item.id);
          }

          return true;
        }
      });
  }

  collectLayerIds(items: TOCItem[]): string[] {
    return items.flatMap((item) => (this.isTOCParent(item) ? this.collectLayerIds(item.children) : [item.id]));
  }

  /**
   * Resolve which download layers have assets in the project boundary and move the empty ones to
   * the picker's outside-area group. A failed lookup flags every candidate as inconclusive and
   * leaves the full list usable.
   */
  async loadLayerAssetPresence(projectId: string) {
    const candidateLayerIds = this.collectLayerIds(this.getTOCLayersConfig('all'));
    if (candidateLayerIds.length === 0) return;

    const requestId = ++this.layerAssetPresenceRequestId;

    try {
      this.isCheckingLayerAssets = true;

      const { presence } = await apiGetLayerAssetPresence(projectId, candidateLayerIds);

      // Superseded by a reset or a newer project's load while this request was in flight
      if (requestId !== this.layerAssetPresenceRequestId) return;

      this.layerIdsWithoutAssets.clear();
      this.layerIdsWithUnknownAssets.clear();
      this.layerAssetPresenceFailed = false;

      Object.entries(presence).forEach(([layerId, state]) => {
        if (state === 'absent') {
          this.layerIdsWithoutAssets.add(layerId);
        } else if (state === 'unknown') {
          this.layerIdsWithUnknownAssets.add(layerId);
        }
      });

      this.rebuildTOCLayers();

      // Hidden layers must not stay selected for submission or drawn on the preview map
      this.layerIdsWithoutAssets.forEach((layerId) => {
        this.selectedLayerIds.delete(layerId);
        layerStore.visibleLayerIds.delete(layerId);
      });
    } catch (e) {
      if (requestId !== this.layerAssetPresenceRequestId) return;

      this.layerIdsWithoutAssets.clear();
      this.layerIdsWithUnknownAssets.clear();
      this.layerAssetPresenceFailed = true;
      candidateLayerIds.forEach((layerId) => {
        this.layerIdsWithUnknownAssets.add(layerId);
      });
      console.warn('Error fetching layer asset presence: ', e);
      reportExportFormDataLoadFailure(projectId, 'layer_asset_presence', e);
    } finally {
      if (requestId === this.layerAssetPresenceRequestId) {
        this.isCheckingLayerAssets = false;
      }
    }
  }

  async determineLayerRestrictions(items: TOCItem[], projectArea: number, restrictions: { [key: string]: number }) {
    // biome-ignore lint/suspicious/useIterableCallbackReturn: preserving existing logic
    items.map((item) => {
      if (this.isTOCParent(item)) {
        // if the parent is utilities, call recursively to check children
        if (item.label === 'Utilities') {
          return this.determineLayerRestrictions(item.children, projectArea, restrictions);
        }

        const restriction = restrictions[item.label];

        // set all children layer ids to restricted if project area > restriction value
        if (projectArea > Number(restriction)) {
          item.children.forEach((child) => {
            // can't access child's id unless we check it's NOT a TOCParent
            if (!this.isTOCParent(child)) {
              this.restrictedLayerIds.add(child.id);
            }
          });
        }
      }
    });
  }

  /**
   * Flatten the section to leaf ids, then apply once. Recursing and assigning per level let an outer
   * frame's pre-recursion snapshot clobber whatever the inner frame wrote, so any section holding a
   * subsection toggled partially or not at all.
   */
  toggleAllSectionLayers(items: TOCItem[], visible: boolean) {
    this.collectLayerIds(items).forEach((layerId) => {
      // Callers can pass any TOCItem[], including outside-area nodes, so absent layers are refused here
      if (visible && this.layerIdsWithoutAssets.has(layerId)) return;

      if (visible) {
        this.selectedLayerIds.add(layerId);
        layerStore.visibleLayerIds.add(toMapPreviewLayerId(layerId));
      } else {
        this.selectedLayerIds.delete(layerId);
        layerStore.visibleLayerIds.delete(toMapPreviewLayerId(layerId));
      }
    });
  }

  /** Tri-state for a section row, derived from its descendant leaves rather than stored. */
  getSectionCheckedState(items: TOCItem[]): boolean | 'indeterminate' {
    const layerIds = this.collectLayerIds(items);
    if (layerIds.length === 0) return false;

    const selectedCount = layerIds.filter((layerId) => this.selectedLayerIds.has(layerId)).length;
    if (selectedCount === 0) return false;

    return selectedCount === layerIds.length ? true : 'indeterminate';
  }

  toggleAllLayers(visible: boolean) {
    if (!visible) {
      // Read the selection before clearing it — it drives which layers come off the map
      this.selectedLayerIds.forEach((layerId) => {
        layerStore.visibleLayerIds.delete(toMapPreviewLayerId(layerId));
      });
      this.selectedLayerIds.clear();
      return;
    }

    // Custom-data and site-data leaves live in the extract picker's extraSections, never in
    // TOCLayers: keep any already-checked ones (covers the window before the available sets sync)
    // and turn on every such layer the picker currently offers.
    const extraLayerIds = [...this.selectedLayerIds].filter(
      (id) => id.startsWith(CUSTOM_DATA_LAYER_PREFIX) || id.startsWith(SITE_DATA_LAYER_ID_PREFIX),
    );

    this.selectedLayerIds.clear();
    const allLayerIds = [
      ...extraLayerIds,
      ...this.availableCustomLayerIds,
      ...this.availableSiteDataLayerIds,
      ...this.collectLayerIds(this.TOCLayers),
    ];
    allLayerIds.forEach((layerId) => {
      this.selectedLayerIds.add(layerId);
      layerStore.visibleLayerIds.add(toMapPreviewLayerId(layerId));
    });
  }

  /** Replace the whole selection, keeping the preview map in step. Refuses absent layers like every bulk toggle. */
  setSelectedLayers(layerIds: string[]) {
    this.toggleAllLayers(false);

    layerIds.forEach((layerId) => {
      if (this.layerIdsWithoutAssets.has(layerId)) return;

      this.selectedLayerIds.add(layerId);
      layerStore.visibleLayerIds.add(toMapPreviewLayerId(layerId));
    });
  }

  toggleAllSections(expanded: boolean) {
    this.expandedSections.clear();

    if (!expanded) return;

    const checkSection = (items: TOCItem[], group: PickerGroup) => {
      items.forEach((item) => {
        if (this.isTOCParent(item)) {
          this.expandedSections.add(expansionKey(group, item));

          checkSection(item.children, group);
        }
      });
    };

    checkSection(this.TOCLayers, 'within');

    // The Custom Data and Site Data sections render from extraSections, not TOCLayers, so "Expand All"
    // must add them here or they stay collapsed until the picker remounts.
    if (this.availableCustomLayerIds.size > 0) {
      this.expandedSections.add(
        expansionKey('extra', { sectionId: CUSTOM_DATA_SECTION_ID, label: CUSTOM_DATA_SECTION_LABEL }),
      );
    }
    if (this.availableSiteDataLayerIds.size > 0) {
      this.expandedSections.add(
        expansionKey('extra', { sectionId: SITE_DATA_SECTION_ID, label: SITE_DATA_SECTION_LABEL }),
      );
    }
  }

  get selectedLayerSections() {
    const sections = new Set<string>();
    this.selectedLayerIds.forEach((layerId) => {
      const section = getTOCParentSection(layerId);
      if (section) {
        sections.add(section);
      }
    });
    return sections;
  }
}

const newDownloadStore = proxy(new NewDowloadStore());
export default newDownloadStore;
